export type OnboardingPurposeKey =
  | 'userName'
  | 'goal'
  | 'motivation'
  | 'founderType'
  | 'startupName'
  | 'ideaAge'
  | 'userAge'
  | 'dailyPhoneHours'
  | 'focusKillers'
  | 'sharpestTime'
  | 'dailySessions'
  | 'focusDuration'
  | 'thirtyDayGoals'
  | 'biggestFears'
  | 'successMeaning'
  | 'commitmentLevel';

export type OnboardingAnswers = Partial<{
  entryIdentity: 'founder' | 'scroller';
  userName: string;
  goal: string;
  motivation: string;
  founderType: string;
  startupName: string;
  ideaAge: string;
  userAge: string;
  dailyPhoneHours: number;
  focusKillers: string[];
  sharpestTime: string;
  dailySessions: string;
  focusDuration: string;
  thirtyDayGoals: string[];
  biggestFears: string[];
  successMeaning: string;
  commitmentLevel: string;
}>;

export type ChoiceAnswerKey =
  | 'goal'
  | 'motivation'
  | 'founderType'
  | 'userAge'
  | 'ideaAge'
  | 'sharpestTime'
  | 'dailySessions'
  | 'focusDuration'
  | 'commitmentLevel';
export type MultiAnswerKey = 'focusKillers' | 'thirtyDayGoals' | 'biggestFears';
export type TextAnswerKey = 'userName' | 'startupName' | 'successMeaning';
export type OnboardingCopy = string | ((answers: OnboardingAnswers) => string);

export type ChoiceStep = {
  answerKey: ChoiceAnswerKey;
  options: readonly string[];
  reactions: Record<string, OnboardingCopy>;
  screen: number;
  title: OnboardingCopy;
};

export type TextStep = {
  answerKey: TextAnswerKey;
  buttonLabel: string;
  multiline?: boolean;
  placeholder: string;
  reaction: OnboardingCopy;
  screen: number;
  subtitle?: string;
  title: OnboardingCopy;
  validatesRequired?: boolean;
};

export type MultiStep = {
  answerKey: MultiAnswerKey;
  options: readonly string[];
  screen: number;
  title: OnboardingCopy;
};

export type OnboardingPersonalizationProfile = {
  blockedAppsCategory: string;
  dailySessionTarget: number;
  defaultFocusDurationMinutes: number;
  emotionalAnchorText: string;
  startupName: string;
};

// Legacy screen ids 17-20, 23, and 25 are intentionally retired. Keep the
// remaining ids stable so historical funnel analytics remain comparable.
export const ONBOARDING_INTRO_SCREEN = 1;
export const ONBOARDING_PITCH_SCREEN = 2;
export const ONBOARDING_FIRST_QUESTION_SCREEN = 5;
export const ONBOARDING_PHONE_HOURS_SCREEN = 9;
export const ONBOARDING_PHONE_MATH_SCREEN = 10;
export const ONBOARDING_RECLAIM_SCREEN = 11;
export const ONBOARDING_REFLECTION_SCREEN = 16;
export const ONBOARDING_TEST_INTRO_SCREEN = 17;
export const ONBOARDING_MINI_SESSION_SCREEN = 18;
export const ONBOARDING_DAY_ONE_SCREEN = 19;
export const ONBOARDING_PLAN_LOADING_SCREEN = 21;
export const ONBOARDING_BATTLE_PLAN_SCREEN = 25;
export const ONBOARDING_PERMISSION_SCREEN = 26;
export const ONBOARDING_NOTIFICATIONS_SCREEN = 27;
export const ONBOARDING_SOCIAL_PROOF_SCREEN = 28;
export const ONBOARDING_FINAL_SCREEN = 29;
export const ONBOARDING_QUESTION_COUNT = ONBOARDING_FINAL_SCREEN;

// The shortened flow deliberately keeps historical ids while changing the
// visible order. Removed screen ids stay retired so historical analytics remain
// comparable without forcing existing stored answers through a migration.
export function getNextOnboardingFlowScreen(screen: number) {
  switch (screen) {
    case 5:
    case 6:
      return 8;
    case ONBOARDING_REFLECTION_SCREEN:
      return 24;
    case 24:
      return ONBOARDING_PLAN_LOADING_SCREEN;
    case ONBOARDING_PLAN_LOADING_SCREEN:
      return ONBOARDING_SOCIAL_PROOF_SCREEN;
    default:
      return screen + 1;
  }
}

export function getPreviousOnboardingFlowScreen(screen: number) {
  switch (screen) {
    case ONBOARDING_FIRST_QUESTION_SCREEN:
      return ONBOARDING_PITCH_SCREEN;
    case 8:
      return 5;
    case 24:
      return ONBOARDING_REFLECTION_SCREEN;
    case ONBOARDING_PLAN_LOADING_SCREEN:
      return 24;
    default:
      return screen - 1;
  }
}

// Hard paywall: only a successful purchase/restore (or an already-Pro user)
// advances past the paywall. Cancel returns to the social-proof screen.
export function getScreenAfterOnboardingPaywall() {
  return ONBOARDING_FINAL_SCREEN;
}

export const CHOICE_STEPS: readonly ChoiceStep[] = [
  {
    answerKey: 'userAge',
    options: ['Under 18', '18–24', '25–34', '35–44', '45+'],
    reactions: {},
    screen: 8,
    title: 'Quick one - how old are you?',
  },
  {
    answerKey: 'sharpestTime',
    options: ['Early morning', 'Mid morning', 'Afternoon', 'Late / night'],
    reactions: {
      'Early morning': (answers) => `5am ${getUserName(answers)}. Terrifying. Efficient. Respect.`,
      'Mid morning': "Coffee kicked in, meetings haven't started. Golden window.",
      Afternoon: 'Post-lunch warrior. Rare breed.',
      'Late / night': (answers) => `Midnight ${getUserName(answers)}. The city sleeps, Bob ships.`,
    },
    screen: 13,
    title: (answers) => `When are you sharpest, ${getUserName(answers)}?`,
  },
  {
    answerKey: 'dailySessions',
    options: ['1', '2', '3', '4+'],
    reactions: {
      '1': "One deep session. That's all it takes. Consistency beats intensity.",
      '2': (answers) =>
        `Morning and afternoon. The classic double. ${getUserName(answers)} is built for this.`,
      '3': (answers) => `Three sessions. ${getUserName(answers)} means business.`,
      '4+': (answers) =>
        `Four sessions. ${getUserName(answers)} is either a machine or lying. Let's find out.`,
    },
    screen: 14,
    title: 'How many focus sessions can you commit to per day?',
  },
  {
    answerKey: 'focusDuration',
    options: ['15 min', '25 min', '45 min', '60 min+'],
    reactions: {
      '15 min': (answers) =>
        `Short and sharp. Better than nothing. We'll build from here, ${getUserName(answers)}.`,
      '25 min': (answers) => `Classic Pomodoro. ${getUserName(answers)} knows what they're doing.`,
      '45 min': (answers) => `Deep work territory. ${getUserName(answers)} is a weapon.`,
      '60 min+': (answers) =>
        `An hour of pure focus? ${getUserName(answers)} is not normal. Bob respects that.`,
    },
    screen: 15,
    title: 'How long before your brain wanders?',
  },
  {
    answerKey: 'commitmentLevel',
    options: [
      'Extremely committed — this has to work',
      "Pretty committed — I'll give it a real shot",
      'Curious — just seeing what this is about',
      'Honestly not sure yet',
    ],
    reactions: {
      'Extremely committed — this has to work': (answers) =>
        `Locked in. ${getStartupName(answers)} doesn't know it yet, but ${getUserName(answers)} just decided it's happening.`,
      "Pretty committed — I'll give it a real shot": (answers) =>
        `That's enough, ${getUserName(answers)}. Consistency beats intensity every time.`,
      'Curious — just seeing what this is about':
        "Fair. Let the app do the convincing. That's what it's here for.",
      'Honestly not sure yet': (answers) =>
        `Honest answer. ${getUserName(answers)} showed up anyway. That already says something.`,
    },
    screen: 24,
    title: (answers) =>
      `How committed are you to making ${getStartupName(answers)} happen, ${getUserName(answers)}?`,
  },
] as const;

export const TEXT_STEPS: readonly TextStep[] = [
  {
    answerKey: 'userName',
    buttonLabel: "THAT'S ME →",
    placeholder: 'Your name',
    reaction: (answers) => `Nice to meet you, ${getUserName(answers)}. Bob's been waiting.`,
    screen: 5,
    subtitle: "Not Bob's. Yours.",
    title: "Before we continue — what's your name?",
    validatesRequired: true,
  },

] as const;

export const MULTI_STEPS: readonly MultiStep[] = [
  {
    answerKey: 'focusKillers',
    options: [
      'Instagram / TikTok',
      'YouTube',
      'Twitter / X',
      'News sites',
      'My own brain',
      'All of the above',
    ],
    screen: 12,
    title: 'What kills your focus the most?',
  },
] as const;

export const CHOICE_STEP_BY_SCREEN = new Map(CHOICE_STEPS.map((step) => [step.screen, step] as const));
export const TEXT_STEP_BY_SCREEN = new Map(TEXT_STEPS.map((step) => [step.screen, step] as const));
export const MULTI_STEP_BY_SCREEN = new Map(MULTI_STEPS.map((step) => [step.screen, step] as const));

const FALLBACK_STARTUP_NAME = 'FocusCorp';
const FALLBACK_EMOTIONAL_ANCHOR = "I'm ready to build something real.";

export function getStartupName(answers: OnboardingAnswers) {
  const value = answers.startupName?.replace(/\s+/g, ' ').trim();
  return value && value.length > 0 ? value : 'your company';
}

export function getUserName(answers: OnboardingAnswers) {
  const value = answers.userName?.replace(/\s+/g, ' ').trim();
  return value && value.length > 0 ? value : 'Bob';
}

export function getStoredStringAnswer(answers: OnboardingAnswers, key: ChoiceAnswerKey | TextAnswerKey) {
  const value = answers[key];
  return typeof value === 'string' ? value : '';
}

export function getStoredArrayAnswer(answers: OnboardingAnswers, key: MultiAnswerKey) {
  const value = answers[key];
  return Array.isArray(value) ? value : [];
}

export function resolveCopy(copy: OnboardingCopy, answers: OnboardingAnswers) {
  return typeof copy === 'function' ? copy(answers) : copy;
}

export function resolveReaction(reaction: OnboardingCopy, answers: OnboardingAnswers) {
  return typeof reaction === 'function' ? reaction(answers) : reaction;
}

// Keep Bob present without interrupting every answer. These checkpoints close
// short 3-4 question batches; the phone-time checkpoint remains attached to
// the dedicated math screen because it is one of the onboarding's key reveals.
export function isQuestionReactionCheckpoint(screen: number) {
  return screen === 6 || screen === 15;
}

export function getQuestionCheckpointReaction(
  screen: number,
  answers: OnboardingAnswers,
  fallbackReaction: OnboardingCopy,
) {
  if (screen === 15) {
    const sessions = answers.dailySessions ?? '1';
    const duration = answers.focusDuration ?? '25 min';
    return `${sessions} x ${duration} daily. Plan set, ${getUserName(answers)}. Bob knows what gets in the way.`;
  }

  return resolveReaction(fallbackReaction, answers);
}

export function getDailyPhoneHours(answers: OnboardingAnswers) {
  const value = answers.dailyPhoneHours;
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(1, Math.min(12, Math.floor(value)))
    : 3;
}

export function getPhoneMathReaction(hours: number, answers: OnboardingAnswers) {
  const yearlyDays = Math.round((hours * 365) / 24);
  const userName = getUserName(answers);

  if (hours <= 2) {
    return `Okay, ${userName} is actually not that bad. Still, even 1 hour a day adds up.`;
  }

  if (hours <= 4) {
    return `That's ${yearlyDays} days a year, ${userName}. Just gone.`;
  }

  if (hours <= 7) {
    return `${userName} is spending more time on the phone than sleeping. That's a problem.`;
  }

  return `${userName}. That's basically a part-time job. A part-time job that pays nothing.`;
}

function hasSelectionPair(selections: readonly string[], first: string, second: string) {
  return selections.includes(first) && selections.includes(second);
}

function getFocusKillersReaction(selections: readonly string[], answers: OnboardingAnswers) {
  const userName = getUserName(answers);
  const count = selections.length;

  if (selections.includes('All of the above')) {
    return `Yeah. That tracks. That's why we're here, ${userName}.`;
  }

  if (count === 1) {
    switch (selections[0]) {
      case 'Instagram / TikTok':
        return `The algorithm is designed by 1,000 engineers to keep ${userName} scrolling. That's the enemy.`;
      case 'YouTube':
        return `One tab. Three hours. YouTube really said 'up next' and ${userName} said 'okay'.`;
      case 'Twitter / X':
        return 'One thread and 45 minutes are gone. Classic.';
      case 'News sites':
        return `The world will still be on fire after the session, ${userName}. Promise.`;
      case 'My own brain':
        return `At least it's honest. We can work with honest, ${userName}.`;
      default:
        return `Bob can work with that, ${userName}.`;
    }
  }

  if (hasSelectionPair(selections, 'Instagram / TikTok', 'YouTube')) {
    return `${userName}, TikTok and YouTube in the same brain. That's not distraction, that's a full-time job.`;
  }

  if (hasSelectionPair(selections, 'Instagram / TikTok', 'Twitter / X')) {
    return `The scroll never ends with those two. ${userName}'s focus doesn't stand a chance.`;
  }

  if (hasSelectionPair(selections, 'YouTube', 'My own brain')) {
    return `The algorithm and the inner monologue. ${userName} is fighting on two fronts.`;
  }

  if (hasSelectionPair(selections, 'News sites', 'Twitter / X')) {
    return `Breaking news plus hot takes. ${userName}'s losing hours to things that won't matter next week.`;
  }

  if (selections.includes('My own brain')) {
    return `The apps and the brain working against ${userName} at the same time. FocusCorp's got both covered.`;
  }

  if (count >= 3) {
    return `${userName} picked ${count} distractions. Honestly at that point just tap 'all of the above' - Bob won't judge.`;
  }

  return `That's the enemy list, ${userName}. Bob is taking notes.`;
}

function getThirtyDayGoalsReaction(selections: readonly string[], answers: OnboardingAnswers) {
  const userName = getUserName(answers);
  const startupName = getStartupName(answers);
  const count = selections.length;

  if (count === 1) {
    switch (selections[0]) {
      case 'Launch something new':
        return `${startupName} ships something new in 30 days. ${userName} said it. Bob believes it.`;
      case 'Get first paying customer':
        return `First dollar is the hardest. ${userName}'s going to earn it.`;
      case 'Land a new client':
        return `One good client changes the quarter. ${userName}'s going to close it.`;
      case 'Grow revenue':
        return `Revenue is the scoreboard. Focused hours are how ${userName} moves it.`;
      case 'Just build the habit':
        return `The habit is the foundation. Everything else follows, ${userName}.`;
      default:
        return `Bob likes the ambition, ${userName}.`;
    }
  }

  if (hasSelectionPair(selections, 'Launch something new', 'Get first paying customer')) {
    return `Ship it AND sell it in 30 days. ${userName}, Bob respects the ambition. That's a tight timeline.`;
  }

  if (hasSelectionPair(selections, 'Launch something new', 'Grow revenue')) {
    return `Launch it, then make it pay. That's the founder double, ${userName}.`;
  }

  if (hasSelectionPair(selections, 'Land a new client', 'Grow revenue')) {
    return `New clients, bigger numbers. ${userName} knows exactly where the hours should go.`;
  }

  if (hasSelectionPair(selections, 'Get first paying customer', 'Land a new client')) {
    return `Selling on two fronts. Every focused hour is a pitch ${userName} doesn't lose.`;
  }

  if (selections.includes('Just build the habit')) {
    return `Good call, ${userName} - the habit is what makes everything else possible.`;
  }

  if (count >= 3) {
    return `${count} goals in 30 days. ${userName}, Bob's not saying no - but let's make sure the sessions actually happen first.`;
  }

  return `Bob has the mission, ${userName}.`;
}

function getBiggestFearsReaction(selections: readonly string[], answers: OnboardingAnswers) {
  const userName = getUserName(answers);
  const count = selections.length;

  if (count === 1) {
    switch (selections[0]) {
      case 'Running out of time':
        return `Then stop wasting it, ${userName}. That's literally why we're here.`;
      case 'Nobody cares about it':
        return "They won't care until you ship. Ship first, validate second.";
      case 'Not being good enough':
        return `Every founder feels this. The ones who win show up anyway, ${userName}.`;
      case 'Giving up halfway':
        return `Noted. ${userName} is not allowed to quit. Bob has receipts now.`;
      default:
        return `Bob heard that, ${userName}.`;
    }
  }

  if (hasSelectionPair(selections, 'Running out of time', 'Giving up halfway')) {
    return `Running out of time AND quitting halfway through. ${userName}, that's one fear: not finishing. Bob's going to fix that.`;
  }

  if (hasSelectionPair(selections, 'Nobody cares about it', 'Not being good enough')) {
    return `Imposter syndrome hitting from both sides. Every real founder feels this, ${userName}. The ones who win ship anyway.`;
  }

  if (hasSelectionPair(selections, 'Running out of time', 'Nobody cares about it')) {
    return `Racing against the clock to build something people might ignore. ${userName}, that's the founder experience. Welcome.`;
  }

  if (hasSelectionPair(selections, 'Not being good enough', 'Giving up halfway')) {
    return `Fear of failure and fear of quitting. ${userName}, those cancel each other out - you can't quit if you're too afraid of failing.`;
  }

  if (hasSelectionPair(selections, 'Running out of time', 'Not being good enough')) {
    return `Not enough time, not enough skill. ${userName}, Bob started with less. The sessions will close the gap.`;
  }

  if (count >= 3) {
    return `${userName}, that's a lot to carry. Good thing Bob doesn't let you carry it alone. One session at a time.`;
  }

  return `Bob wrote that down, ${userName}.`;
}

export function getMultiReaction(
  key: MultiAnswerKey,
  selections: readonly string[],
  answers: OnboardingAnswers,
) {
  switch (key) {
    case 'focusKillers':
      return getFocusKillersReaction(selections, answers);
    case 'thirtyDayGoals':
      return getThirtyDayGoalsReaction(selections, answers);
    case 'biggestFears':
      return getBiggestFearsReaction(selections, answers);
  }
}

export function buildPersistedAnswers(answers: OnboardingAnswers, options: {omitMissingName?:boolean} = {}): OnboardingAnswers {
  return {
    ...(answers.entryIdentity === 'founder' || answers.entryIdentity === 'scroller' ? { entryIdentity: answers.entryIdentity } : {}),
    ...(options.omitMissingName && !answers.userName?.trim() ? {} : {userName:getUserName(answers)}),
    goal: answers.goal ?? '',
    motivation: answers.motivation ?? '',
    founderType: answers.founderType ?? '',
    startupName: answers.startupName?.replace(/\s+/gu, ' ').trim() ?? '',
    ideaAge: answers.ideaAge ?? '',
    userAge: answers.userAge ?? '',
    dailyPhoneHours: getDailyPhoneHours(answers),
    focusKillers: answers.focusKillers ?? [],
    sharpestTime: answers.sharpestTime ?? '',
    dailySessions: answers.dailySessions ?? '',
    focusDuration: answers.focusDuration ?? '',
    thirtyDayGoals: answers.thirtyDayGoals ?? [],
    biggestFears: answers.biggestFears ?? [],
    successMeaning: answers.successMeaning?.trim() ?? '',
    commitmentLevel: answers.commitmentLevel ?? '',
  };
}

export function getInitialAnswers(initialState: { answers: OnboardingAnswers }): OnboardingAnswers {
  return {
    ...initialState.answers,
    dailyPhoneHours: getDailyPhoneHours(initialState.answers),
  };
}

export function getOnboardingTextAnswer(
  answers: OnboardingAnswers,
  purposeKey: OnboardingPurposeKey,
) {
  const value = answers[purposeKey];
  return typeof value === 'string' ? value.trim() : '';
}

function sanitizeStartupName(value: string | undefined) {
  const normalizedValue = value?.replace(/\s+/g, ' ').trim() ?? '';
  return normalizedValue.length > 0 ? normalizedValue : FALLBACK_STARTUP_NAME;
}

function sanitizeEmotionalAnchor(value: string | undefined) {
  const normalizedValue = value?.replace(/\s+/g, ' ').trim() ?? '';
  return normalizedValue.length > 0 ? normalizedValue : FALLBACK_EMOTIONAL_ANCHOR;
}

function getFocusDurationMinutes(value: string | undefined) {
  switch (value) {
    case '15 min':
      return 15;
    case '25 min':
      return 25;
    case '45 min':
      return 45;
    case '60 min+':
      return 60;
    default:
      return 25;
  }
}

function getDailySessionTarget(value: string | undefined) {
  switch (value) {
    case '1':
      return 1;
    case '2':
      return 2;
    case '3':
      return 3;
    case '4+':
      return 4;
    default:
      return 1;
  }
}

function getBlockedAppsCategory(value: string | undefined) {
  switch (value) {
    case 'Instagram / TikTok':
      return 'short-form social';
    case 'Twitter / X':
      return 'social feed scroll';
    case 'Youtube':
      return 'video recommendations';
    case 'My own brain':
      return 'internal distractions';
    case 'All of the above':
      return 'full distraction block';
    default:
      return 'full distraction block';
  }
}

function getBlockedAppsCategoryFromValues(values: string[] | undefined) {
  if (!values || values.length === 0) {
    return 'full distraction block';
  }

  if (values.includes('All of the above')) {
    return 'full distraction block';
  }

  if (values.includes('Instagram / TikTok') || values.includes('YouTube')) {
    return 'algorithmic feeds';
  }

  if (values.includes('My own brain')) {
    return 'internal distractions';
  }

  return getBlockedAppsCategory(values[0]);
}

export function buildOnboardingPersonalizationProfile(
  answers: OnboardingAnswers,
): OnboardingPersonalizationProfile {
  return {
    blockedAppsCategory: getBlockedAppsCategoryFromValues(answers.focusKillers),
    dailySessionTarget: getDailySessionTarget(answers.dailySessions),
    defaultFocusDurationMinutes: getFocusDurationMinutes(answers.focusDuration),
    emotionalAnchorText: sanitizeEmotionalAnchor(answers.successMeaning),
    startupName: sanitizeStartupName(answers.startupName),
  };
}

export function buildOnboardingFinalLine(answers: OnboardingAnswers) {
  const { startupName } = buildOnboardingPersonalizationProfile(answers);
  return `${startupName}. Okay. Now it's real.`;
}
